import type { UpcomingBirthday } from '../api/types';

/**
 * Счёт по дням рождения — на клиенте, а не на сервере.
 *
 * «Сегодня» у человека своё: сервер, отдающий «осталось 3 дня» по своему календарю,
 * ошибается на сутки у всех, кто живёт восточнее или западнее его. Поэтому сервер отдаёт
 * факт (день и месяц), а вывод из него делается здесь, по часам устройства.
 *
 * 29 февраля в невисокосном году считается наступившим 28-го — то же решение, что и на
 * сервере (`BirthdayDates`). Вариант «1 марта» отодвигает поздравление на день позже
 * настоящего, а «пропустить год» — не вариант вовсе.
 */

/**
 * Возрастные границы даты рождения (заказ владельца 2026-08-30).
 *
 * <p>Раньше единственной границей было «не из будущего и не старше 120 лет» — то есть сегодняшняя
 * дата проходила проверку, и в профиле мог стоять день рождения новорождённого. Возраст в карточке
 * контакта после такого показывал ноль, а напоминание уходило друзьям в тот же день.
 *
 * <p>Нижняя граница — четырнадцать лет (REL-8, поднята с семи 2026-08-30). Это не «возраст, с
 * которого пользуются самостоятельно», а граница, ниже которой приложение с открытой перепиской
 * попадает под COPPA (до 13) и под «Семейную политику» Google Play со всеми её требованиями.
 * Четырнадцать выводит SVYAZ из этой категории целиком, а не впритык, и совпадает с числом в
 * условиях использования. Верхняя — сто: она отсекает «1092» и подобные промахи, оставаясь выше
 * любого реального возраста.
 *
 * <p>Те же два числа проверяет сервер (`UserProfileService.validBirthDate`): клиентская проверка
 * здесь ради понятного сообщения на своём языке, а не вместо серверной.
 */
export const BIRTH_AGE_MIN_YEARS = 14;
export const BIRTH_AGE_MAX_YEARS = 100;

/**
 * Сдвиг назад на годы с прижатием 29 февраля к 28-му.
 *
 * `new Date(y - 7, 1, 29)` в невисокосный год молча стал бы 1 марта, и граница уехала бы на сутки
 * в сторону послабления. Java на сервере (`LocalDate.minusYears`) прижимает к 28-му — здесь то же.
 */
function minusYears(from: Date, years: number): Date {
  const year = from.getFullYear() - years;
  const month = from.getMonth();
  const lastDayOfMonth = new Date(year, month + 1, 0).getDate();
  return new Date(year, month, Math.min(from.getDate(), lastDayOfMonth));
}

/**
 * Крайние допустимые даты рождения на сегодня — обе включительно.
 *
 * `max` — самая поздняя из разрешённых (сегодня исполняется ровно {@link BIRTH_AGE_MIN_YEARS}),
 * `min` — самая ранняя (ровно {@link BIRTH_AGE_MAX_YEARS}).
 */
function birthDateBounds(now: Date = new Date()): { min: Date; max: Date } {
  const today = startOfLocalDay(now);
  return {
    min: minusYears(today, BIRTH_AGE_MAX_YEARS),
    max: minusYears(today, BIRTH_AGE_MIN_YEARS),
  };
}

/** Те же границы в ISO — их ждут атрибуты `min`/`max` у календаря в вебе. */
export function birthDateBoundsIso(now: Date = new Date()): { min: string; max: string } {
  const { min, max } = birthDateBounds(now);
  return { min: localDateKey(min), max: localDateKey(max) };
}

/** Почему ввод отвергнут: разобрать не удалось вовсе — или дата разобрана, но возраст вне границ. */
type BirthDateInputRejection = 'format' | 'age';

type BirthDateInputResult =
  | { ok: true; iso: string }
  | { ok: false; reason: BirthDateInputRejection };

/**
 * Разбор даты рождения из поля ввода с указанием причины отказа.
 *
 * <p>Причина нужна форме: «введите ДД.ММ.ГГГГ» в ответ на аккуратно набранное «01.02.2024» ничего
 * человеку не объясняет — формат-то он угадал, не угадал он границу возраста.
 *
 * <p>Разбор строгий: «31.02.1990» отвергается, а не превращается молча в 3 марта, как сделал бы
 * `new Date`. Молчаливая подмена здесь стоит дороже обычного — человек увидел бы поздравления
 * не в свой день и не понял бы почему.
 *
 * @returns при успехе — ISO `yyyy-MM-dd`, либо `''` для пустого ввода, то есть «дату сняли»
 */
export function classifyBirthDateInput(raw: string, now: Date = new Date()): BirthDateInputResult {
  const text = raw.trim();
  if (text.length === 0) return { ok: true, iso: '' };
  const m = /^(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{4})$/.exec(text);
  if (!m) return { ok: false, reason: 'format' };
  const day = Number(m[1]);
  const month = Number(m[2]);
  const year = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return { ok: false, reason: 'format' };
  const d = new Date(year, month - 1, day);
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) {
    return { ok: false, reason: 'format' };
  }
  const { min, max } = birthDateBounds(now);
  if (d.getTime() > max.getTime() || d.getTime() < min.getTime()) {
    return { ok: false, reason: 'age' };
  }
  return { ok: true, iso: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}` };
}

/**
 * Дата рождения из поля ввода в форму, которую ждёт сервер.
 *
 * @returns ISO `yyyy-MM-dd`, либо `null` для непустого, но неразобранного ввода; для пустого
 *     ввода — `''`, то есть «дату сняли»
 */
export function parseBirthDateInput(raw: string, now: Date = new Date()): string | null | '' {
  const res = classifyBirthDateInput(raw, now);
  return res.ok ? res.iso : null;
}

/** Обратный показ: ISO из профиля в то, что человек печатал. */
export function formatBirthDateInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  return m ? `${m[3]}.${m[2]}.${m[1]}` : '';
}

/** Локальная дата устройства в форме `yyyy-MM-dd` — то, что уходит на сервер как `today`. */
export function localDateKey(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Полночь местного дня: все сравнения ниже идут по датам, а не по моментам. */
function startOfLocalDay(now: Date): Date {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

/**
 * Дата дня рождения в указанном году.
 *
 * 29 февраля в невисокосном году сдвигается на 28-е: `new Date(2027, 1, 29)` молча стал бы
 * 1 марта, и поздравление опоздало бы на сутки.
 */
function birthdayInYear(month: number, day: number, year: number): Date {
  const lastDayOfMonth = new Date(year, month, 0).getDate();
  return new Date(year, month - 1, Math.min(day, lastDayOfMonth));
}

/**
 * Сколько полных дней осталось до ближайшего дня рождения; 0 — сегодня.
 *
 * Сегодняшний день считается ближайшим, а не «уже прошедшим»: иначе в сам праздник ответ был
 * бы «через 365 дней», и поздравлять было бы некого.
 */
export function daysUntilBirthday(
  birthMonth: number,
  birthDay: number,
  now: Date = new Date(),
): number {
  const today = startOfLocalDay(now);
  let next = birthdayInYear(birthMonth, birthDay, today.getFullYear());
  if (next.getTime() < today.getTime()) {
    next = birthdayInYear(birthMonth, birthDay, today.getFullYear() + 1);
  }
  // Через полдень, а не через полночь: переход на летнее время между этими датами делает
  // сутки 23- или 25-часовыми, и деление «в лоб» теряет или добавляет день.
  const msPerDay = 24 * 60 * 60 * 1000;
  const diff = next.getTime() + 12 * 60 * 60 * 1000 - (today.getTime() + 12 * 60 * 60 * 1000);
  return Math.round(diff / msPerDay);
}

/** У кого день рождения сегодня — те, ради кого показывается напоминание с поздравлением. */
export function birthdaysToday(
  rows: UpcomingBirthday[],
  now: Date = new Date(),
): UpcomingBirthday[] {
  return rows.filter((r) => daysUntilBirthday(r.birthMonth, r.birthDay, now) === 0);
}

/**
 * Ближайшие, кроме сегодняшних: список «скоро», который живёт рядом с напоминанием и не
 * повторяет его.
 */
export function birthdaysSoon(
  rows: UpcomingBirthday[],
  withinDays: number,
  now: Date = new Date(),
): UpcomingBirthday[] {
  return rows
    .map((r) => ({ row: r, days: daysUntilBirthday(r.birthMonth, r.birthDay, now) }))
    .filter((x) => x.days > 0 && x.days <= withinDays)
    .sort((a, b) => a.days - b.days)
    .map((x) => x.row);
}
